import EntityFilter from "src/components/common/EntityFilter";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { ReactNode, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import {
  fundActionByStatus,
  fundPaymentEntityTabs,
  fundPaymentTabs,
  fundPaymentTabsIO,
  fundStatus,
  FundActionActor,
} from "src/constants/common";
import {
  getFundApplicationById,
  getFundRequest,
} from "src/utils/api/createProjectFlow";
import ViewImages from "src/components/common/ViewImage";
import {
  dateAndTimeStamp,
  downloadSheet,
  formatToIndianCurrency,
} from "src/utils/helper";
import defaultStyles from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import { useAppSelector } from "src/redux/store";
import FundStatusTimeline from "../ProjectDetailView/FundApplicationDetails/FundStatusTimeline";
import {
  FUND_SECTIONS,
  hasFundBeenReleased,
} from "../ProjectDetailView/FundApplicationDetails/FundApplicationUI";

/** Append-only status-change trail for the fund application (same timeline
 * used on the full Fund Application Details page). */
export const StatusHistory = ({ statusHistory }: { statusHistory?: any[] }) => (
  <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 2, mt: 2 }}>
    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, mb: 1 }}>
      Status History
    </Typography>
    {statusHistory?.length ? (
      <FundStatusTimeline statusHistory={statusHistory} />
    ) : (
      <Box sx={{ fontSize: "0.8rem", color: "#999" }}>No history yet.</Box>
    )}
  </Box>
);

/**
 * Which form on the FA details page each status hands the SNO. Used to deep
 * link the Action cell straight to that panel.
 */
const SECTION_BY_STATUS: Record<string, string> = {
  CREATED: FUND_SECTIONS.forward,
  UNDER_QUERY: FUND_SECTIONS.forward,
  APPROVED: FUND_SECTIONS.receipt,
  PAYMENT_ACKNOWLEDGED: FUND_SECTIONS.uc,
  UC_REJECTED: FUND_SECTIONS.uc,
};

/** The Action cell's clickable text — a row offers at most one. */
const ActionLink = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <Box
    sx={{
      color: "#16A085",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "normal",
      lineHeight: 1.3,
      "&:hover": { textDecoration: "underline" },
    }}
    onClick={onClick}
  >
    {children}
  </Box>
);

const FundApplication = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const tab = searchParams.get("currentTab");

  const [tableRows, setTableRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const [activeTab, setActiveTab] = useState(tab ?? undefined);
  const [activeYearTab, setActiveYearTab] = useState<number | undefined>(
    undefined
  );

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewRow, setViewRow] = useState<any>(null);

  const { data: viewData, isLoading: isViewLoading } = useQuery(
    ["viewFundApplication", viewRow?.id],
    () => getFundApplicationById(viewRow?.id),
    { enabled: isViewModalOpen && !!viewRow?.id }
  );

  const user = useAppSelector((state) => state.root.user);

  const role = user.data.userType;

  // The fund application flow runs IO -> SNO -> Admin. Entities (ORGANIZATION)
  // only observe it, so they own no action in any status.
  const currentActor: FundActionActor | null =
    role === "INDIVIDUAL"
      ? "SNO"
      : role === "ORGANIZATION"
      ? null
      : "IMPLEMENTING_OFFICER";

  const [queryParams, setQueryParams] = useState<{
    status: string;
    offset: number;
    limit: number;
    download: boolean;
    sortBy: string;
    searchText: string;
    yearOfFundApplication: number | undefined;
    entityIds?: string;
  }>({
    status:
      role === "ORGANIZATION" ? "APPROVED,PAYMENT_ACKNOWLEDGED" : activeTab,
    offset: 0,
    // One page at a time. This used to fetch 500 rows and paginate them in the
    // browser, so every load pulled ten pages of fully-hydrated fund
    // applications to show fifty. Export is unaffected — it makes its own
    // full-size request (see exportToExcelEntity).
    limit: 50,
    download: false,
    sortBy: "updated_at:DESC",
    searchText: "",
    yearOfFundApplication: undefined,
  });

  const fundYearTabs = [
    { label: "All", value: undefined },
    { label: "FA 1", value: 1 },
    { label: "FA 2", value: 2 },
    { label: "FA 3", value: 3 },
    { label: "FA 4", value: 4 },
    { label: "FA 5", value: 5 },
    { label: "FA 6", value: 6 },
    { label: "FA 7", value: 7 },
    { label: "FA 8", value: 8 },
    { label: "FA 9", value: 9 },
    { label: "FA 10", value: 10 },
  ];

  // Shared by the grid and the Export button so both render a row the same way.
  const mapFundRequestRow = (row) => ({
    data: row,
    id: row.id,
    sno: row?.serialNo,
    costEstimateID: row?.landOwnershipEpid,
    registrationNo: row?.landOwnership?.projectDetail?.registrationNo,
    advanceWorkCost: formatToIndianCurrency(row?.advanceWorkCost),
    monitoringCost: formatToIndianCurrency(row?.monitoringCost),
    contingencyCost: formatToIndianCurrency(row?.contingencyCost),
    totalCost: formatToIndianCurrency(row?.totalCost),
    totalAmountReleased: formatToIndianCurrency(row?.totalAmountReleased),
    estimatedAdvanceWorkCost: formatToIndianCurrency(
      row?.estimatedAdvanceWorkCost
    ),
    estimatedMonitoringCost: formatToIndianCurrency(
      row?.estimatedMonitoringCost
    ),
    estimatedContingencyCost: formatToIndianCurrency(
      row?.estimatedContingencyCost
    ),
    totalEstimatedCost: formatToIndianCurrency(row?.totalEstimatedCost),
    ecoRestorationAmount:
      formatToIndianCurrency(row?.landOwnership?.demandNote) ?? "-",
    division: row?.landOwnership?.projectDetail?.landSpecification.division,
    entityName: row?.landOwnership?.user?.firstName,
    totalArea: `${row?.landOwnership?.netPlantationArea}`,
    remarks: row?.remark,
    status: row?.status,
    updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
    created_at: dateAndTimeStamp(row?.created_at, "IST"),
  });

  const { isLoading, isFetching } = useQuery(
    ["users", queryParams],
    () =>
      getFundRequest({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(res?.data?.rows.map(mapFundRequestRow));
        setTotalRecords(res?.data?.count ?? 0);
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const RenderView = (params) => {
    const faYear = params?.row?.data?.yearOfFundApplication;
    const isLatestFa = params?.row?.data?.isLatestFundApplication;
    // The FA number badge is only meaningful when viewing across years, so it
    // is shown only under the "All" FA-year tab. The latest FA is highlighted
    // in purple, older ones in orange.
    const showFaBadge = activeYearTab === undefined && !!faYear;
    const badgeColor = isLatestFa ? "#8E44AD" : "#E67E22";
    const badgeBg = isLatestFa
      ? "rgba(142, 68, 173, 0.12)"
      : "rgba(230, 126, 34, 0.12)";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box
          component={RouterLink}
          to={`${path.fundApplicationDetails}?${createSearchParams({
            paymentId: params.row.id,
          })}`}
          sx={{ ...defaultStyles.renderButtonText2 }}
        >{`${params?.row?.id}`}</Box>
        {showFaBadge && (
          <Box
            component="span"
            sx={{
              color: badgeColor,
              backgroundColor: badgeBg,
              fontWeight: 700,
              fontSize: "11px",
              lineHeight: 1,
              padding: "3px 6px",
              borderRadius: "4px",
              fontStyle: "italic",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {`FA${faYear}`}
          </Box>
        )}
      </Box>
    );
  };

  // Where the application currently stands — a plain label, not a control.
  // Anything actionable lives in the Action column next to it.
  const RenderStatus = (params) => {
    const status = params?.row?.status;

    return (
      <Box
        sx={{
          color: "text.primary",
          fontWeight: 500,
          whiteSpace: "normal",
          lineHeight: 1.3,
        }}
      >
        {fundStatus[status] ?? status ?? "--"}
      </Box>
    );
  };

  // What this user can do about that status. Each status hands the application
  // to exactly one user type (see fundActionByStatus) — everyone else, entities
  // included, gets a blank cell and reads the status only.
  const RenderAction = (params) => {
    const status = params?.row?.status;
    const epid = params?.row?.costEstimateID;
    const action = fundActionByStatus[status];

    // A UC-approved FA whose successor has already been raised has nothing
    // left to do on it.
    const hasNextFundApplication =
      status === "UC_APPROVED" && !params?.row?.data?.isLatestFundApplication;

    // A released fund with no payment receipt on record stays the SNO's to
    // file, however far the application has since travelled. Where the status'
    // own action belongs to someone else, that upload is what this cell offers.
    const missingPaymentReceipt =
      currentActor === "SNO" &&
      hasFundBeenReleased(status) &&
      !params?.row?.data?.paymentAcknowledgeDocId;

    const noAction = <Box sx={{ color: "text.disabled" }}>--</Box>;

    if (!action || action.actor !== currentActor || hasNextFundApplication) {
      if (!missingPaymentReceipt) {
        return noAction;
      }

      return (
        <ActionLink
          onClick={() =>
            navigate(
              `${path.fundApplicationDetails}?${createSearchParams({
                paymentId: params.row.id,
                section: FUND_SECTIONS.receipt,
              })}`
            )
          }
        >
          Upload Payment Receipt
        </ActionLink>
      );
    }

    return (
      <ActionLink
        onClick={() => {
          if (status === "UC_APPROVED") {
            navigate(
              `${path.fillFundApplication}?${createSearchParams({
                landOwnershipEpid: epid,
              })}`
            );
            return;
          }

          if (status === "NODAL_UNDER_QUERY") {
            // IO replies to the SNO's query.
            navigate(
              `${path.viewFundApplicationDetails}?${createSearchParams({
                paymentId: params.row.id,
              })}`
            );
            return;
          }

          // Everything else is a form on the FA details page: land on the one
          // this action belongs to rather than at the top of the page.
          navigate(
            `${path.fundApplicationDetails}?${createSearchParams({
              paymentId: params.row.id,
              section: SECTION_BY_STATUS[status] ?? "",
            })}`
          );
        }}
      >
        {action.label}
      </ActionLink>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "id",
      headerName: "Fund Application ID",
      width: 220,
      sortable: false,
      renderCell: RenderView,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "statusLabel",
      headerName: "Status",
      width: 200,
      sortable: false,
      renderCell: RenderStatus,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      sortable: false,
      renderCell: RenderAction,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "costEstimateID",
      headerName: "Cost Estimate ID",
      width: 220,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "division",
      headerName: "Division",
      width: 150,
      sortable: true,
      userType: ["INDIVIDUAL", "ORGANIZATION"],
      tab: [],
    },
    {
      field: "totalArea",
      headerName: "Net Planned Area(Ha)",
      width: 150,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "contingencyCost",
      headerName: "Contingency Cost",
      width: 200,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "totalCost",
      headerName: "Total Cost",
      width: 200,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "totalAmountReleased",
      headerName: "Total Amount Released",
      width: 200,
      sortable: false,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 200,
      sortable: true,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
    {
      field: "updated_at",
      headerName: "UPDATED AT",
      width: 200,
      sortable: true,
      userType: ["INDIVIDUAL", null, "ORGANIZATION"],
      tab: [],
    },
  ];

  // The grid is server-paginated, so Export cannot reuse its rows — it makes
  // its own request for the full filtered result set, exactly as the admin
  // portal does. Filters, tab and sort are inherited from queryParams so the
  // sheet always matches what is on screen.
  const exportToExcelEntity = async () => {
    setIsExporting(true);
    try {
      const response = await getFundRequest({
        ...queryParams,
        offset: 0,
        limit: totalRecords || 10000,
        download: false,
      });
      downloadSheet((response?.data?.rows ?? []).map(mapFundRequestRow));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (sortByField) => {
    setPaginationModel({ pageSize: 50, page: 0 });
    setQueryParams({
      ...queryParams,
      offset: 0,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      offset: newPaginationModel.page * newPaginationModel.pageSize,
      limit: newPaginationModel.pageSize,
    }));
  };

  const columns = useMemo(() => {
    const filtered = tableColumns.filter((item) => {
      return item.userType.includes(role) && !!!item.tab.includes(activeTab);
    });
    console.log(
      "Filtered columns:",
      filtered.map((c) => c.headerName)
    );
    console.log("Current role:", role);
    console.log("Active tab:", activeTab);
    return filtered;
  }, [queryParams, role, activeTab]);

  const selectedTab =
    role === "INDIVIDUAL"
      ? fundPaymentTabs
      : role === "ORGANIZATION"
      ? fundPaymentEntityTabs
      : fundPaymentTabsIO;

  return (
    <DashboardWrapper
      title="Fund Application (FA)"
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!tableRows.length}
    >
      <>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              padding: "10px 1%",
              background: "white",
            }}
          >
            <SearchBar
              getText={(e) => {
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((a) => {
                  return { ...queryParams, searchText: `${e}`, offset: 0 };
                });
              }}
              customStyles={{
                wrapper: {
                  flex: 1,
                  minWidth: "240px",
                },
              }}
            />
            <EntityFilter
              source="fund"
              filters={{
                status: queryParams.status,
                yearOfFundApplication: queryParams.yearOfFundApplication,
                searchText: queryParams.searchText,
              }}
              value={queryParams.entityIds?.split(",") ?? []}
              onChange={(ids) => {
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                setQueryParams((prev) => ({
                  ...prev,
                  entityIds: ids.length ? ids.join(",") : undefined,
                  offset: 0,
                }));
              }}
            />
          </Box>
          {/* FA Year Tabs */}
          <Box
            sx={{
              padding: "0 1%",
              background: "white",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <HorizontalTabs
              tabHeaders={fundYearTabs.map((t) => ({
                label: t.label,
                value: t.value !== undefined ? String(t.value) : "",
              }))}
              tabIndex={activeYearTab !== undefined ? activeYearTab : 0}
              onChange={(val) => {
                const year = val ? Number(val) : undefined;
                setActiveYearTab(year);
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  yearOfFundApplication: year,
                  offset: 0,
                }));
              }}
              components={fundYearTabs.map(() => (
                <></>
              ))}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "13%",
              padding: "10px 1%",
              background: "white",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          >
            <HorizontalTabs
              tabHeaders={selectedTab}
              tabIndex={selectedTab.findIndex(
                (item) => item.value === activeTab
              )}
              onChange={(e) => {
                setActiveTab(e);
                navigate(
                  `${path.fundApplication}${e ? `?currentTab=${e}` : ""}`
                );
                setPaginationModel({ pageSize: 50, page: 0 });
                setQueryParams((prev) => ({
                  ...prev,
                  status: e,
                  offset: 0,
                }));
              }}
              components={[<></>, <></>]}
            />
            <Button
              onClick={exportToExcelEntity}
              text={isExporting ? "Exporting..." : "Export"}
              disabled={isExporting}
            />
          </Box>
          {isLoading || isFetching ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : !!tableRows.length ? (
            <DataGrid
              rows={tableRows || []}
              columns={columns}
              rowCount={totalRecords}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              onPageChange={(newPage) =>
                handlePaginationChange({ ...paginationModel, page: newPage })
              }
              onPageSizeChange={(newPageSize) =>
                handlePaginationChange({
                  ...paginationModel,
                  pageSize: newPageSize,
                })
              }
              rowsPerPageOptions={[50]}
              sortingMode="server"
              onSortModelChange={handleSort}
              paginationMode="server"
              columnBuffer={20}
              loading={isLoading || isFetching}
              getRowId={(row) => `${row.id}-${row.userName}`}
            />
          ) : (
            <EmptyView
              heading="No Entry Found"
              subheading=""
              onClick={() => {}}
              buttonText={""}
            />
          )}
        </Box>

        {/* View Uploaded Documents Modal */}
        {(() => {
          const raw = viewRow?.data ?? {};
          const fd = viewData?.data?.fundDetails ?? {};
          const ecoRestorationCost = Number(
            raw?.landOwnership?.demandNote ?? 0
          );
          // Aggregates from the FA detail endpoint (same as the upload modal).
          const totalPaymentReleased = Number(
            viewData?.data?.totalPaymentReleasedTillDate ?? 0
          );
          const previousFAPayment = Number(
            viewData?.data?.previousFaPaymentReleased ?? 0
          );
          const financialProgress = Number(
            viewData?.data?.totalFinancialProgress ?? 0
          );
          const utilized = Number(fd?.utilizedAmount ?? 0);
          const balanceForRevalidation = previousFAPayment - utilized;
          const needsRevalidationLetter = balanceForRevalidation > 0;

          const readOnlyFieldSx = {
            "& .MuiInputBase-input": { bgcolor: "#f9f9f9" },
          };
          const highlightedFieldSx = {
            "& .MuiInputBase-input": {
              bgcolor: "#e8f5f2",
              color: "#16A085",
              fontWeight: 600,
            },
          };

          const ucDocUrl = viewData?.data?.utilizationCertificateDocUrl;
          const revalidationDocUrl = viewData?.data?.revalidationLetterDocUrl;

          return (
            <Dialog
              open={isViewModalOpen}
              onClose={() => {
                setIsViewModalOpen(false);
                setViewRow(null);
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle
                sx={{ bgcolor: "#16A085", color: "white", fontWeight: 600 }}
              >
                Fund Application Details
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {isViewLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Row 1 */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          Total Eco-restoration Cost (INR)(as per cost estimate)
                        </Box>
                        <TextField
                          value={
                            formatToIndianCurrency(ecoRestorationCost) ?? "-"
                          }
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          Total Payment Released till date (INR)
                        </Box>
                        <TextField
                          value={
                            formatToIndianCurrency(totalPaymentReleased) ?? "-"
                          }
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                    </Box>

                    {/* Row 2 */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          Payment Released in previous Fund application (INR)
                        </Box>
                        <TextField
                          value={
                            formatToIndianCurrency(previousFAPayment) ?? "-"
                          }
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          Utilized Amount (INR as per audited UC)
                        </Box>
                        <TextField
                          value={formatToIndianCurrency(utilized) ?? "-"}
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          UC Date
                        </Box>
                        <TextField
                          value={
                            fd?.ucDate ? String(fd.ucDate).slice(0, 10) : "-"
                          }
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                    </Box>

                    {/* Row 3 */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            mb: 0.5,
                          }}
                        >
                          Financial Progress as per Portal (INR)
                        </Box>
                        <TextField
                          value={
                            formatToIndianCurrency(financialProgress) ?? "-"
                          }
                          InputProps={{ readOnly: true }}
                          fullWidth
                          size="small"
                          sx={readOnlyFieldSx}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 3, maxWidth: "50%", pr: 1 }}>
                      <Box
                        sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5 }}
                      >
                        Balance Amount (INR) for revalidation
                      </Box>
                      <TextField
                        value={
                          formatToIndianCurrency(balanceForRevalidation) ?? "0"
                        }
                        InputProps={{ readOnly: true }}
                        fullWidth
                        size="small"
                        sx={highlightedFieldSx}
                      />
                    </Box>

                    {/* Uploaded documents */}
                    <Box
                      sx={{
                        borderTop: "1px solid #e0e0e0",
                        pt: 2,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 3,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1 }}
                        >
                          Utilization Certificate
                        </Box>
                        {ucDocUrl ? (
                          <ViewImages fileUrl={ucDocUrl} label="" />
                        ) : (
                          <Box
                            sx={{
                              fontSize: "0.8rem",
                              color: "text.secondary",
                              p: 1,
                              bgcolor: "#f9f9f9",
                              borderRadius: 1,
                            }}
                          >
                            No document available
                          </Box>
                        )}
                      </Box>
                      {needsRevalidationLetter && (
                        <Box>
                          <Box
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              mb: 1,
                            }}
                          >
                            Revalidation Letter
                          </Box>
                          {revalidationDocUrl ? (
                            <ViewImages fileUrl={revalidationDocUrl} label="" />
                          ) : (
                            <Box
                              sx={{
                                fontSize: "0.8rem",
                                color: "text.secondary",
                                p: 1,
                                bgcolor: "#f9f9f9",
                                borderRadius: 1,
                              }}
                            >
                              No document available
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    <StatusHistory
                      statusHistory={viewData?.data?.statusHistory}
                    />
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setViewRow(null);
                  }}
                  text="Close"
                />
              </DialogActions>
            </Dialog>
          );
        })()}
      </>
    </DashboardWrapper>
  );
};

export default FundApplication;
